import { Theme, createTheme } from '@mui/material/styles';
import { cloneDeep, get, merge, set } from 'lodash';
import { useEffect, useState } from 'react';

import Constants from '../../group/constants';
import { DefaultSkinThemeOptions } from '../../shared/services/SkinThemeService';
import { DomainDesignAsset } from '../../design/models/DomainDesignAsset';
import { SkinTheme } from '../../design/models/SkinTheme';
import ThemeService from '../../shared/services/ThemeService';
import { singletonHook } from 'react-singleton-hook';

/** Skin theme colors to be transferred to theme. */
const AUGMENT_COLORS = {
  adminCardColor: 'group.adminCardColor',
  reviewerCardColor: 'group.reviewerCardColor',
  memberCardColor: 'group.memberCardColor'
};

/**
 * Augment the themes palette with colors from the skin theme.
 * @param muiThemes The theme to be augmented. The original is not modified.
 * @param skinThemes Skin theme where to source additional palette colors.
 */
const { palette } = createTheme();
const augmentThemes = (muiThemes: object, skinThemes: SkinTheme): object => {
  const copy = cloneDeep(muiThemes);
  Object.keys(AUGMENT_COLORS).forEach((color) => {
    const main =
      get(skinThemes, AUGMENT_COLORS[color]) ||
      Constants.GroupCardDefaultBackgroundColor.MEMBER;
    set(copy, `palette.${color}`, palette.augmentColor({ color: { main } }));
  });
  return copy;
};

/** Regular expression matching a URL, perhaps nested in quotes. */
const reUrl = /(?:^|"|')(https?:\/\/.+?)(?:'|"|$)/;

/** Sanitize the skin theme, e.g. stripping the URL from a font link. */
const sanitizeSkinTheme = (skinTheme: SkinTheme): SkinTheme => {
  const sanitized = cloneDeep(skinTheme);
  const match = sanitized?.container?.fontImport?.match(reUrl);
  if (match) {
    sanitized.container.fontImport = match[1];
  }
  return sanitized;
};

declare module '@mui/material/styles' {
  interface TypographyVariants {
    custom1: React.CSSProperties;
    custom2: React.CSSProperties;
  }

  interface TypographyVariantsOptions {
    custom1?: React.CSSProperties;
    custom2?: React.CSSProperties;
  }
}

// Allow using it in the Typography component
declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    custom1: true;
    custom2: true;
  }
}
const baseTheme = createTheme();

const customTypography = {
  ...baseTheme.typography,
  custom1: {
    fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
    fontWeight:400,
    fontSize: '17px',
    lineHeight: 1.42,
    letterSpacing:''
  },
  custom2: {
    fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
    fontWeight:400,
    fontSize: '20px',
    lineHeight: 1.42,
    letterSpacing:''
  }
};

    const customMuiTheme = createTheme({
      ...baseTheme,
      typography: customTypography
  });

function useThemes() {
  /** Value indicating whether the theme is loading. */
  const [loading, setLoading] = useState(true);
  /** Error message encountered during saving. */
  const [error, setError] = useState<string>();
  /** Current MUI theme, including defaults. */
  const [muiTheme, _setMuiTheme] = useState<Theme>(createTheme({
    ...baseTheme,
    typography: customTypography
  }));
  /** MUI theme as loaded from server and edited by user. */
  const [muiLocalTheme, _setMuiLocalTheme] = useState<object>();
  /** Current skin theme. */
  const [skinTheme, _setSkinTheme] = useState(DefaultSkinThemeOptions);

  const _setSkinThemeSanitized = (skinTheme: SkinTheme) => {
    _setSkinTheme(sanitizeSkinTheme(skinTheme));
  };

  const setModel = (asset: DomainDesignAsset) => {
    _setSkinThemeSanitized(asset.skinThemes);
    _setMuiTheme(createTheme(augmentThemes((merge({}, customMuiTheme, asset.muiThemes)), asset.skinThemes)));
    _setMuiLocalTheme(merge({}, customMuiTheme, asset.muiThemes));
  };

  useEffect(() => {
    setLoading(true);
    setError(undefined);
    ThemeService.getThemeModel()
      .then((model) => {
        setModel(model.assets);
        setLoading(false);
      })
      .catch((reason) => setError(`${reason}`));
  }, []);

  useEffect(() => {
    const match = reUrl.exec(skinTheme?.container?.fontImport);
    if (match) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = match[1];
      document.getElementsByTagName('head')[0].appendChild(link);
      return () => {
        link.parentElement?.removeChild(link);
      };
    }
  }, [skinTheme?.container?.fontImport]);

  return {
    loading,
    muiTheme,
    skinTheme,
    error,
    clearError: () => setError(undefined),
    // For editing - clients should call useThemesEdit hook.
    _setMuiTheme,
    _setSkinTheme: _setSkinThemeSanitized,
    muiLocalTheme,
    _setMuiLocalTheme
  };
}

interface IUseThemes {
  loading: boolean;
  muiTheme: Theme;
  skinTheme: SkinTheme;
  error: string;
  clearError: () => void;
  // For editing - clients should call useThemesEdit hook.
  _setMuiTheme: (theme: Theme) => void;
  _setSkinTheme: (skinTheme: SkinTheme) => void;
  muiLocalTheme: object;
  _setMuiLocalTheme: (theme: object) => void;
}

const init = {
  muiTheme: customMuiTheme,
  skinTheme: {}
} as IUseThemes;


// Singleton hook - consider using Redux store.
export default singletonHook<IUseThemes>(init, useThemes);
